import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Search } from "lucide-react";
import { HeroLens, useHeroLens } from "./HeroLens";
import { MaskLines } from "../motion/Reveal";
import { LuxButton } from "../common/Primitives";
import { IMG } from "../../config/site";
import { scrollToId } from "../../lib/api";
import { useUI } from "../../context/UIContext";

const ease = [0.22, 1, 0.36, 1];
const fade = (d) => ({ initial: { opacity: 0, y: 18 }, animate: { opacity: 1, y: 0 }, transition: { duration: 1.2, ease, delay: d } });

export const Hero = () => {
  const ref = useRef(null);
  const { openEnquiry } = useUI();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "10%"]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const imgRef = useRef(null);
  const { lens, onMove, hide } = useHeroLens(imgRef);

  return (
    <section id="home" ref={ref} data-testid="hero-section" onMouseMove={onMove} onMouseLeave={hide} onWheel={hide}
      className={`relative flex flex-col overflow-hidden bg-[#21170F] lg:block lg:h-[100svh] lg:max-h-[980px] lg:min-h-[680px] ${lens ? "cursor-zoom-in" : ""}`}>
      <div className="relative order-2 h-[64svh] min-h-[420px] overflow-hidden lg:absolute lg:inset-y-0 lg:right-0 lg:h-auto lg:w-[min(100%,calc(clamp(680px,100svh,980px)*1.49))]">
        <motion.div style={{ y }} className="absolute inset-0 will-change-transform">
          <picture>
          <source media="(max-width: 639px)" srcSet={IMG.heroMobile} />
          <motion.img ref={imgRef} src={IMG.hero} alt="A TOYAM Alkaline Water 750 ml glass bottle on a marble terrace table at sunset, with the Taj Mahal softly in the background"
            fetchPriority="high" initial={{ scale: 1.12 }} animate={{ scale: 1 }} transition={{ duration: 2.8, ease }}
            className="h-full w-full object-cover object-center sm:object-[62%_center]" />
          </picture>
        </motion.div>
        <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-[#21170F] to-transparent lg:hidden" />
        <div className="absolute inset-0 hidden bg-[linear-gradient(90deg,#21170F_0%,rgba(33,23,15,.86)_28%,rgba(33,23,15,.25)_55%,transparent_75%)] lg:block" />
        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#21170F]/50 to-transparent" />
        <div className="absolute inset-x-0 top-0 hidden h-44 bg-gradient-to-b from-[#21170F]/75 to-transparent lg:block" />
        <motion.div aria-hidden="true" className="absolute inset-0 origin-top bg-[#21170F]"
          initial={{ scaleY: 1 }} animate={{ scaleY: 0 }} transition={{ duration: 1.4, ease, delay: 0.1 }} />
      </div>

      <motion.div style={{ y: textY }} className="relative z-10 order-1 mx-auto w-full max-w-[1440px] px-5 pb-10 pt-32 sm:px-8 lg:flex lg:h-full lg:items-center lg:px-12 lg:pb-0 lg:pt-0">
        <div className="max-w-[640px]">
          <motion.p {...fade(0.6)} className="eyebrow">Premium water. Exceptional hospitality.</motion.p>
          <h1 className="mt-6 text-[2.9rem] leading-[0.98] text-[#F8F3E9] sm:text-6xl lg:text-[4.3rem] xl:text-[5.4rem]" data-testid="hero-title">
            <MaskLines lines={["The Water Behind", "Exceptional", "Hospitality"]} delay={0.75} />
          </h1>
          <motion.p {...fade(1.35)} className="font-serif-lux mt-6 text-2xl italic text-[#E8D6B7] sm:text-[1.7rem]">Crafted in Agra. Inspired by Hospitality.</motion.p>
          <motion.p {...fade(1.5)} className="mt-5 max-w-md text-[15px] leading-[1.8] text-[#F8F3E9]/80">
            Premium alkaline water in glass bottles, designed for distinguished hotels, restaurants and resorts.
          </motion.p>
          <motion.div {...fade(1.65)} className="mt-10 flex flex-col gap-4 sm:flex-row">
            <LuxButton arrow onClick={() => openEnquiry("Hotel supply partnership")} data-testid="hero-partner-btn">Partner with TOYAM</LuxButton>
            <LuxButton variant="line" onClick={() => scrollToId("bottle")} data-testid="hero-explore-btn">Explore the bottle</LuxButton>
          </motion.div>
        </div>
      </motion.div>

      <motion.button type="button" {...fade(2.1)} onClick={() => scrollToId("story")} data-testid="hero-scroll-indicator" aria-label="Scroll to Our Story"
        className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-3 text-[10px] uppercase tracking-[0.4em] text-[#F8F3E9]/70 lg:flex">
        Scroll
        <span className="relative h-14 w-px overflow-hidden bg-[#F8F3E9]/20"><span className="scroll-cue absolute inset-0 bg-[#B18A4A]" /></span>
      </motion.button>
      <motion.p {...fade(2.3)} className="absolute bottom-8 right-12 z-10 hidden items-center gap-2 rounded-full bg-[#21170F]/60 px-4 py-2 text-[10px] uppercase tracking-[0.3em] text-[#F8F3E9]/90 backdrop-blur-md lg:flex" data-testid="hero-lens-hint">
        <Search className="h-3.5 w-3.5 text-[#B18A4A]" strokeWidth={1.5} aria-hidden="true" />Hover the bottle to inspect the label
      </motion.p>
      <HeroLens lens={lens} src={IMG.hero} />
    </section>
  );
};
